import { useEffect, useRef, useState, useCallback } from "react";
import { useParallax } from "@/hooks/useParallax";

interface LiveAttack {
  id: number;
  ip: string;
  attackType: string;
  attackTypeClass: string;
  attackMsg: string;
  sourceCountry: string;
  sourceFlag: string;
  targetCountry: string;
  targetFlag: string;
  severity: string;
  severityClass: string;
  nodeLocation: string;
  nodeFlag: string;
  time: string;
}

interface AnimatedAttack {
  id: number;
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  progress: number;
  color: string;
  speed: number;
}

interface City {
  name: string;
  x: number;
  y: number;
  flag: string;
  country: string;
  region: string;
  status: "Active" | "Standby" | "Alert";
}

const cities: City[] = [
  { name: "Lima", x: 0.26, y: 0.62, flag: "PE", country: "Peru", region: "Americas", status: "Active" },
  { name: "Santiago", x: 0.28, y: 0.78, flag: "CL", country: "Chile", region: "Americas", status: "Active" },
  { name: "Bogota", x: 0.27, y: 0.52, flag: "CO", country: "Colombia", region: "Americas", status: "Alert" },
  { name: "Mexico City", x: 0.21, y: 0.44, flag: "MX", country: "Mexico", region: "Americas", status: "Active" },
  { name: "New York", x: 0.29, y: 0.33, flag: "US", country: "USA", region: "Americas", status: "Active" },
  { name: "London", x: 0.47, y: 0.27, flag: "GB", country: "UK", region: "Europe", status: "Active" },
  { name: "Berlin", x: 0.51, y: 0.25, flag: "DE", country: "Germany", region: "Europe", status: "Standby" },
  { name: "Moscow", x: 0.56, y: 0.21, flag: "RU", country: "Russia", region: "Europe", status: "Alert" },
  { name: "Beijing", x: 0.78, y: 0.34, flag: "CN", country: "China", region: "Asia", status: "Alert" },
  { name: "Tokyo", x: 0.86, y: 0.36, flag: "JP", country: "Japan", region: "Asia", status: "Active" },
  { name: "Sao Paulo", x: 0.34, y: 0.72, flag: "BR", country: "Brazil", region: "Americas", status: "Active" },
  { name: "Dubai", x: 0.62, y: 0.46, flag: "AE", country: "UAE", region: "Asia", status: "Standby" },
  { name: "Mumbai", x: 0.69, y: 0.48, flag: "IN", country: "India", region: "Asia", status: "Active" },
  { name: "Singapore", x: 0.77, y: 0.55, flag: "SG", country: "Singapore", region: "Asia", status: "Active" },
  { name: "Sydney", x: 0.90, y: 0.76, flag: "AU", country: "Australia", region: "Oceania", status: "Standby" },
  { name: "Paris", x: 0.48, y: 0.29, flag: "FR", country: "France", region: "Europe", status: "Active" },
  { name: "Madrid", x: 0.45, y: 0.34, flag: "ES", country: "Spain", region: "Europe", status: "Active" },
  { name: "Istanbul", x: 0.57, y: 0.35, flag: "TR", country: "Turkey", region: "Europe", status: "Alert" },
  { name: "Johannesburg", x: 0.53, y: 0.75, flag: "ZA", country: "South Africa", region: "Africa", status: "Standby" },
  { name: "Cairo", x: 0.55, y: 0.43, flag: "EG", country: "Egypt", region: "Africa", status: "Active" },
];

const connections: [number, number][] = [
  [0, 1], [0, 2], [0, 10], [0, 3], [3, 4], [4, 5], [5, 6], [6, 7],
  [7, 8], [8, 9], [9, 13], [13, 12], [12, 11], [11, 5], [5, 15], [15, 6],
  [14, 13], [10, 1], [2, 3], [16, 5], [17, 11], [18, 11], [19, 11],
];

const attackTypeDefs = [
  { type: "Malware", msg: "Virus upload attempt", color: "#ff1a1a", bgClass: "bg-rose-500/15 text-rose-300 border-rose-500/20" },
  { type: "MITM", msg: "Communication interception", color: "#ff1a1a", bgClass: "bg-rose-500/15 text-rose-300 border-rose-500/20" },
  { type: "DDoS", msg: "Traffic overwhelming servers", color: "#ff1a1a", bgClass: "bg-rose-500/15 text-rose-300 border-rose-500/20" },
  { type: "SQL Injection", msg: "Malicious SQL statements", color: "#f59e0b", bgClass: "bg-amber-500/15 text-amber-300 border-amber-500/20" },
  { type: "Drive-by", msg: "Unsolicited malware download", color: "#ef4444", bgClass: "bg-red-500/15 text-red-300 border-red-500/20" },
  { type: "Phishing", msg: "Fraudulent info request", color: "#f59e0b", bgClass: "bg-amber-500/15 text-amber-300 border-amber-500/20" },
  { type: "Trojan", msg: "Misleading malware", color: "#ff1a1a", bgClass: "bg-rose-500/15 text-rose-300 border-rose-500/20" },
  { type: "API Attack", msg: "Targeting API endpoints", color: "#ff1a1a", bgClass: "bg-rose-500/15 text-rose-300 border-rose-500/20" },
  { type: "XSS", msg: "Client-side script injection", color: "#00d26a", bgClass: "bg-emerald-500/15 text-emerald-300 border-emerald-500/20" },
  { type: "Brute Force", msg: "Automated login attempts", color: "#10b981", bgClass: "bg-emerald-500/15 text-emerald-300 border-emerald-500/20" },
  { type: "Ransomware", msg: "File encryption demand", color: "#ff1a1a", bgClass: "bg-rose-500/15 text-rose-300 border-rose-500/20" },
  { type: "Exfiltration", msg: "Unauthorized data transfer", color: "#f59e0b", bgClass: "bg-amber-500/15 text-amber-300 border-amber-500/20" },
];

const severityDefs = [
  { label: "Critical", color: "#ff1a1a", textClass: "text-rose-400" },
  { label: "High", color: "#f97316", textClass: "text-orange-400" },
  { label: "Medium", color: "#f59e0b", textClass: "text-amber-400" },
  { label: "Low", color: "#10b981", textClass: "text-emerald-400" },
];

const allCountries = [
  { name: "Peru", flag: "PE" }, { name: "Chile", flag: "CL" }, { name: "Colombia", flag: "CO" },
  { name: "Mexico", flag: "MX" }, { name: "USA", flag: "US" }, { name: "UK", flag: "GB" },
  { name: "Germany", flag: "DE" }, { name: "Russia", flag: "RU" }, { name: "China", flag: "CN" },
  { name: "Japan", flag: "JP" }, { name: "Brazil", flag: "BR" }, { name: "UAE", flag: "AE" },
  { name: "India", flag: "IN" }, { name: "Singapore", flag: "SG" }, { name: "Australia", flag: "AU" },
  { name: "France", flag: "FR" }, { name: "Spain", flag: "ES" }, { name: "Turkey", flag: "TR" },
  { name: "South Africa", flag: "ZA" }, { name: "Egypt", flag: "EG" }, { name: "Iraq", flag: "IQ" },
  { name: "Iran", flag: "IR" }, { name: "Pakistan", flag: "PK" }, { name: "South Korea", flag: "KR" },
  { name: "Indonesia", flag: "ID" }, { name: "Canada", flag: "CA" }, { name: "Ukraine", flag: "UA" },
  { name: "Poland", flag: "PL" }, { name: "Italy", flag: "IT" }, { name: "Netherlands", flag: "NL" },
];

const flagMap: Record<string, string> = {
  PE: "\uD83C\uDDF5\uD83C\uDDEA", CL: "\uD83C\uDDE8\uD83C\uDDF1", CO: "\uD83C\uDDE8\uD83C\uDDF4",
  MX: "\uD83C\uDDF2\uD83C\uDDFD", US: "\uD83C\uDDFA\uD83C\uDDF8", GB: "\uD83C\uDDEC\uD83C\uDDE7",
  DE: "\uD83C\uDDE9\uD83C\uDDEA", RU: "\uD83C\uDDF7\uD83C\uDDFA", CN: "\uD83C\uDDE8\uD83C\uDDF3",
  JP: "\uD83C\uDDEF\uD83C\uDDF5", BR: "\uD83C\uDDE7\uD83C\uDDF7", AE: "\uD83C\uDDE6\uD83C\uDDEA",
  IN: "\uD83C\uDDEE\uD83C\uDDF3", SG: "\uD83C\uDDF8\uD83C\uDDEC", AU: "\uD83C\uDDE6\uD83C\uDDFA",
  FR: "\uD83C\uDDEB\uD83C\uDDF7", ES: "\uD83C\uDDEA\uD83C\uDDF8", TR: "\uD83C\uDDF9\uD83C\uDDF7",
  ZA: "\uD83C\uDDFF\uD83C\uDDE6", EG: "\uD83C\uDDEA\uD83C\uDDEC", IQ: "\uD83C\uDDEE\uD83C\uDDF6",
  IR: "\uD83C\uDDEE\uD83C\uDDF7", PK: "\uD83C\uDDF5\uD83C\uDDF0", KR: "\uD83C\uDDF0\uD83C\uDDF7",
  ID: "\uD83C\uDDEE\uD83C\uDDE9", CA: "\uD83C\uDDE8\uD83C\uDDE6", UA: "\uD83C\uDDFA\uD83C\uDDE6",
  PL: "\uD83C\uDDF5\uD83C\uDDF1", IT: "\uD83C\uDDEE\uD83C\uDDF9", NL: "\uD83C\uDDF3\uD83C\uDDF1",
};

function randomIP() {
  return `${Math.floor(Math.random() * 256)}.${Math.floor(Math.random() * 256)}.${Math.floor(Math.random() * 256)}.${Math.floor(Math.random() * 256)}`;
}

function getStatusColor(status: string) {
  switch (status) {
    case "Active": return { dot: "bg-emerald-400", text: "text-emerald-300", bg: "bg-emerald-500/10" };
    case "Alert": return { dot: "bg-rose-400", text: "text-rose-300", bg: "bg-rose-500/10" };
    default: return { dot: "bg-amber-400", text: "text-amber-300", bg: "bg-amber-500/10" };
  }
}

export default function ThreatMap() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { ref: bgRef, offset } = useParallax(0.1);
  const containerRef = useRef<HTMLDivElement>(null);
  const staticCanvasRef = useRef<HTMLCanvasElement>(null);
  const animCanvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const attacksRef = useRef<AnimatedAttack[]>([]);
  const attackIdRef = useRef(0);
  const lastSpawnRef = useRef(0);
  const dimsRef = useRef({ w: 0, h: 0, dpr: 1 });

  const [liveAttacks, setLiveAttacks] = useState<LiveAttack[]>([]);
  const [stats, setStats] = useState({ active: 0, blocked: 0, critical: 0, total: 0 });
  const [isPanelOpen, setIsPanelOpen] = useState(true);

  const [hoveredCity, setHoveredCity] = useState<City | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [mapDims, setMapDims] = useState({ w: 0, h: 0 });

  const spawnAttack = useCallback(() => {
    const from = cities[Math.floor(Math.random() * cities.length)];
    let to = cities[Math.floor(Math.random() * cities.length)];
    let attempts = 0;
    while (to.name === from.name && attempts < 10) {
      to = cities[Math.floor(Math.random() * cities.length)];
      attempts++;
    }

    const typeDef = attackTypeDefs[Math.floor(Math.random() * attackTypeDefs.length)];
    const sevDef = severityDefs[Math.floor(Math.random() * severityDefs.length)];
    const srcCountry = allCountries[Math.floor(Math.random() * allCountries.length)];
    const tgtCountry = allCountries[Math.floor(Math.random() * allCountries.length)];
    const nodeCountry = allCountries[Math.floor(Math.random() * allCountries.length)];

    const newAttack: AnimatedAttack = {
      id: ++attackIdRef.current,
      sx: from.x,
      sy: from.y,
      tx: to.x,
      ty: to.y,
      progress: 0,
      color: typeDef.color,
      speed: 0.005 + Math.random() * 0.005,
    };

    if (attacksRef.current.length > 15) {
      attacksRef.current.shift();
    }
    attacksRef.current.push(newAttack);

    const liveAttack: LiveAttack = {
      id: newAttack.id,
      ip: randomIP(),
      attackType: typeDef.type,
      attackTypeClass: typeDef.bgClass,
      attackMsg: typeDef.msg,
      sourceCountry: srcCountry.name,
      sourceFlag: srcCountry.flag,
      targetCountry: tgtCountry.name,
      targetFlag: tgtCountry.flag,
      severity: sevDef.label,
      severityClass: sevDef.textClass,
      nodeLocation: nodeCountry.name,
      nodeFlag: nodeCountry.flag,
      time: new Date().toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    };

    setLiveAttacks((prev) => {
      const next = [liveAttack, ...prev];
      return next.slice(0, 25);
    });

    setStats((prev) => ({
      active: prev.active + 1,
      blocked: prev.blocked + (Math.random() > 0.3 ? 1 : 0),
      critical: prev.critical + (sevDef.label === "Critical" ? 1 : 0),
      total: prev.total + 1,
    }));
  }, []);

  // Fade-in on scroll
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  // Measure map dimensions for city overlay positioning
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const rect = container.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = Math.floor(w * 0.55);
      setMapDims({ w, h });
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Draw static connections and city labels once
  useEffect(() => {
    const staticCanvas = staticCanvasRef.current;
    const container = containerRef.current;
    if (!staticCanvas || !container) return;

    const ctx = staticCanvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    const resizeStatic = () => {
      const rect = container.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = Math.floor(w * 0.55);
      dimsRef.current = { w, h, dpr };

      staticCanvas.width = w * dpr;
      staticCanvas.height = h * dpr;
      staticCanvas.style.width = `${w}px`;
      staticCanvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx.clearRect(0, 0, w, h);

      // Connection lines
      ctx.strokeStyle = "rgba(34, 211, 238, 0.15)";
      ctx.lineWidth = 1.2;
      for (const [i, j] of connections) {
        const c1 = cities[i];
        const c2 = cities[j];
        ctx.beginPath();
        ctx.moveTo(c1.x * w, c1.y * h);
        ctx.lineTo(c2.x * w, c2.y * h);
        ctx.stroke();
      }

      // City nodes — static base (we draw lighter since interactive overlay handles the hover states)
      for (const city of cities) {
        const cx = city.x * w;
        const cy = city.y * h;

        ctx.beginPath();
        ctx.arc(cx, cy, 16, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(34, 211, 238, 0.04)";
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 6, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(34, 211, 238, 0.25)";
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(cx, cy, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(165, 243, 252, 0.6)";
        ctx.fill();

        ctx.font = "600 11px var(--font-label), monospace";
        ctx.fillStyle = "rgba(180, 200, 220, 0.45)";
        ctx.textAlign = "center";
        ctx.fillText(city.name, cx, cy - 14);
      }
    };

    resizeStatic();
    window.addEventListener("resize", resizeStatic);
    return () => window.removeEventListener("resize", resizeStatic);
  }, []);

  // Animated attacks canvas
  useEffect(() => {
    const animCanvas = animCanvasRef.current;
    const container = containerRef.current;
    if (!animCanvas || !container) return;

    const ctx = animCanvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;

    const resizeAnim = () => {
      const rect = container.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = Math.floor(w * 0.55);
      animCanvas.width = w * dpr;
      animCanvas.height = h * dpr;
      animCanvas.style.width = `${w}px`;
      animCanvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resizeAnim();
    window.addEventListener("resize", resizeAnim);

    const draw = () => {
      const { w, h } = dimsRef.current;
      if (!w || !h) {
        animRef.current = requestAnimationFrame(draw);
        return;
      }

      ctx.clearRect(0, 0, w, h);
      const now = performance.now();

      if (now - lastSpawnRef.current > 1500) {
        lastSpawnRef.current = now;
        spawnAttack();
      }

      const active = attacksRef.current;
      for (let i = active.length - 1; i >= 0; i--) {
        const a = active[i];
        a.progress += a.speed;

        if (a.progress >= 1) {
          active.splice(i, 1);
          continue;
        }

        const cx = a.sx * w + (a.tx * w - a.sx * w) * a.progress;
        const cy = a.sy * h + (a.ty * h - a.sy * h) * a.progress;

        const hex = a.color;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);

        // Trail line
        const trailOpacity = 0.75 * (1 - a.progress);
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${trailOpacity})`;
        ctx.lineWidth = a.progress < 0.25 ? 3 : 2;
        ctx.setLineDash([8, 5]);
        ctx.beginPath();
        ctx.moveTo(a.sx * w, a.sy * h);
        ctx.lineTo(cx, cy);
        ctx.stroke();
        ctx.setLineDash([]);

        // Full line with subtle glow
        ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.15 * (1 - a.progress)})`;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(a.sx * w, a.sy * h);
        ctx.lineTo(a.tx * w, a.ty * h);
        ctx.stroke();

        // Animated dot with pulse
        const pulse = 1 + Math.sin(now * 0.008 + a.id) * 0.35;
        const dotSize = 4.5 * pulse;

        // Outer glow ring
        ctx.beginPath();
        ctx.arc(cx, cy, dotSize + 12, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.15)`;
        ctx.fill();

        // Inner glow
        ctx.beginPath();
        ctx.arc(cx, cy, dotSize + 4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.35)`;
        ctx.fill();

        // Core dot
        ctx.beginPath();
        ctx.arc(cx, cy, dotSize, 0, Math.PI * 2);
        ctx.fillStyle = hex;
        ctx.fill();

        // White center
        ctx.beginPath();
        ctx.arc(cx, cy, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.8)";
        ctx.fill();
      }

      // Pulsing city nodes overlay (dynamic)
      for (const city of cities) {
        const cx = city.x * w;
        const cy = city.y * h;
        const pulse = 0.4 + 0.4 * Math.sin(now * 0.002 + city.x * 15);

        ctx.beginPath();
        ctx.arc(cx, cy, 14 + pulse * 6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(34, 211, 238, ${0.12 * pulse})`;
        ctx.fill();
      }

      // Scan line
      const scanY = (now * 0.015) % h;
      ctx.fillStyle = "rgba(6, 182, 212, 0.02)";
      ctx.fillRect(0, scanY - 1, w, 2);

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resizeAnim);
      cancelAnimationFrame(animRef.current);
    };
  }, [spawnAttack]);

  const handleCityMouseEnter = (city: City, e: React.MouseEvent) => {
    setHoveredCity(city);
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleCityMouseMove = (e: React.MouseEvent) => {
    // Boundary check to keep tooltip within viewport
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tooltipW = 200; // approximate
    const tooltipH = 80;
    let x = e.clientX + 16;
    let y = e.clientY - 16;
    if (x + tooltipW > vw) x = e.clientX - tooltipW - 16;
    if (y + tooltipH > vh) y = e.clientY - tooltipH - 16;
    if (y < 0) y = 8;
    setTooltipPos({ x, y });
  };

  const handleCityMouseLeave = () => {
    setHoveredCity(null);
  };

  const handleCityClick = (city: City) => {
    setSelectedCity((prev) => (prev?.name === city.name ? null : city));
  };

  // Generate fake node stats based on city
  const getNodeStats = (city: City) => {
    const base = city.name.charCodeAt(0) + city.name.charCodeAt(1);
    return {
      attacksLastHour: (base % 40) + 5,
      blocked: (base % 30) + 3,
      activeNow: (base % 8) + 1,
      uptime: "99." + (base % 99).toString().padStart(2, "0") + "%",
    };
  };

  // Click outside to close node panel
  useEffect(() => {
    if (!selectedCity) return;
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-node-panel]")) return;
      if (target.closest("button[type='button']")) return;
      setSelectedCity(null);
    };
    document.addEventListener("click", handleDocClick);
    return () => document.removeEventListener("click", handleDocClick);
  }, [selectedCity]);

  return (
    <section
      id="threat-map"
      ref={sectionRef}
      className={`relative bg-[#0a0a14] overflow-hidden transition-all duration-1000 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {/* Top accent line */}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-primary-500/40 to-transparent" />

      {/* Humanized ambient background with subtle parallax */}
      <div ref={bgRef} className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Cybersecurity%20analysts%20team%20collaborating%20in%20a%20modern%20operations%20center%2C%20warm%20human%20centered%20atmosphere%2C%20professional%20editorial%20photography%2C%20soft%20depth%20of%20field%2C%20cinematic%20lighting&width=900&height=506&seq=threatmap-human-bg-01&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="absolute -top-[10%] left-0 w-full h-[120%] object-cover object-center opacity-[0.09]"
          style={{ transform: `translateY(${offset}px)` }}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a14] via-[#0a0a14]/70 to-[#0a0a14]" />
      </div>

      {/* Header */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-10 pt-8 md:pt-12 pb-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent-400/20 bg-accent-500/8 mb-4">
              <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
              <span className="text-[10px] font-label font-semibold text-accent-300 uppercase tracking-[0.2em]">
                Live Threat Map
              </span>
            </div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-50">
              Ciberamenazas{" "}
              <span className="bg-gradient-to-r from-accent-300 via-primary-400 to-accent-400 bg-clip-text text-transparent">
                en Tiempo Real
              </span>
            </h2>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-5 md:gap-8">
            {[
              { label: "Active", value: stats.active, color: "text-accent-300", icon: "ri-radar-line" },
              { label: "Blocked", value: stats.blocked, color: "text-emerald-400", icon: "ri-shield-check-line" },
              { label: "Critical", value: stats.critical, color: "text-rose-400", icon: "ri-alert-line" },
              { label: "Total", value: stats.total, color: "text-primary-300", icon: "ri-global-line" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className={`font-heading font-bold text-lg md:text-xl ${stat.color}`}>
                  {stat.value.toLocaleString()}
                </p>
                <p className="text-[10px] font-label text-foreground-500/50 uppercase tracking-wider flex items-center gap-1 justify-center">
                  <i className={stat.icon} />
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div ref={containerRef} className="relative z-10 w-full px-4 sm:px-6 lg:px-10">
        <div className="relative rounded-xl overflow-hidden border border-foreground-100/5">
          {/* Corner brackets */}
          <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-primary-400/30 z-30 pointer-events-none" />
          <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-primary-400/30 z-30 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-primary-400/30 z-30 pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-primary-400/30 z-30 pointer-events-none" />

          {/* Background: real world map image (native lazy img so it only downloads near the viewport) */}
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20minimalist%20world%20map%20with%20subtle%20country%20borders%20outlines%2C%20very%20dark%20navy%20black%20background%2C%20continents%20in%20slightly%20lighter%20dark%20blue%20gray%2C%20no%20text%20no%20labels%20cities%20or%20names%2C%20clean%20vector%20style%20cartography%2C%20subtle%20faint%20grid%20lines%2C%20professional%20cybersecurity%20threat%20map%20aesthetic%2C%20coastline%20edge%20glow%20highlights%2C%20vector%20illustration%20style&width=1600&height=880&seq=threatmap-world-bg-v4&orientation=landscape"
            srcSet="https://readdy.ai/api/search-image?query=Dark%20minimalist%20world%20map%20with%20subtle%20country%20borders%20outlines%2C%20very%20dark%20navy%20black%20background%2C%20continents%20in%20slightly%20lighter%20dark%20blue%20gray%2C%20no%20text%20no%20labels%20cities%20or%20names%2C%20clean%20vector%20style%20cartography%2C%20subtle%20faint%20grid%20lines%2C%20professional%20cybersecurity%20threat%20map%20aesthetic%2C%20coastline%20edge%20glow%20highlights%2C%20vector%20illustration%20style%201280wquery=Dark%20minimalist%20world%20map%20with%20subtle%20country%20borders%20outlines%2C%20very%20dark%20navy%20black%20background%2C%20continents%20in%20slightly%20lighter%20dark%20blue%20gray%2C%20no%20text%20no%20labels%20cities%20or%20names%2C%20clean%20vector%20style%20cartography%2C%20subtle%20faint%20grid%20lines%2C%20professional%20cybersecurity%20threat%20map%20aesthetic%2C%20coastline%20edge%20glow%20highlights%2C%20vector%20illustration%20style%20640w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1280&width=640&height=352&seq=threatmap-world-bg-v4&orientation=landscape&height=704&seq=threatmap-world-bg-v4&orientation=landscape"
            sizes="100vw"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-center z-0"
            loading="lazy"
            decoding="async"
          />

          {/* Static canvas: connections + city nodes base */}
          <canvas ref={staticCanvasRef} className="absolute inset-0 z-10 pointer-events-none" />

          {/* Animated canvas: attacks + pulse overlays */}
          <canvas ref={animCanvasRef} className="absolute inset-0 z-20 pointer-events-none" />

          {/* Interactive city nodes overlay */}
          <div className="absolute inset-0 z-30 pointer-events-none">
            {cities.map((city) => {
              const status = getStatusColor(city.status);
              return (
                <button
                  key={city.name}
                  type="button"
                  className="absolute pointer-events-auto group"
                  style={{
                    left: `${city.x * 100}%`,
                    top: `${city.y * 100}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                  onMouseEnter={(e) => handleCityMouseEnter(city, e)}
                  onMouseMove={handleCityMouseMove}
                  onMouseLeave={handleCityMouseLeave}
                  onClick={() => handleCityClick(city)}
                >
                  {/* Hit area */}
                  <div className="w-10 h-10 relative flex items-center justify-center cursor-pointer">
                    {/* Pulse ring */}
                    <span className={`absolute w-5 h-5 rounded-full ${status.dot} opacity-30 animate-ping`} />
                    {/* Core */}
                    <span className={`relative w-2.5 h-2.5 rounded-full ${status.dot} ring-2 ring-white/20 shadow-[0_0_8px_rgba(34,211,238,0.4)] group-hover:ring-white/40 group-hover:shadow-[0_0_14px_rgba(34,211,238,0.6)] transition-all duration-300`} />
                    {/* Hover ring */}
                    <span className="absolute w-8 h-8 rounded-full border border-cyan-400/0 group-hover:border-cyan-400/40 transition-all duration-300" />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selected city detail panel */}
          {selectedCity && mapDims.w > 0 && (
            <div
              data-node-panel
              className="absolute z-30 pointer-events-auto"
              style={{
                left: `${Math.min(selectedCity.x * 100 + 4, 70)}%`,
                top: `${Math.max(selectedCity.y * 100 - 8, 2)}%`,
              }}
            >
              <div className="bg-background-950/95 backdrop-blur-md border border-foreground-100/10 rounded-xl p-4 w-56 shadow-2xl shadow-black/40">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg leading-none">{flagMap[selectedCity.flag] || "\uD83C\uDF10"}</span>
                    <div>
                      <p className="text-sm font-heading font-semibold text-foreground-50">{selectedCity.name}</p>
                      <p className="text-[10px] text-foreground-400/50">{selectedCity.country}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setSelectedCity(null); }}
                    className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10 text-foreground-400/50 hover:text-foreground-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>

                <div className="flex items-center gap-1.5 mb-3">
                  <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(selectedCity.status).dot}`} />
                  <span className={`text-[11px] font-label font-semibold uppercase tracking-wider ${getStatusColor(selectedCity.status).text}`}>
                    Nodo {selectedCity.status}
                  </span>
                </div>

                {(() => {
                  const s = getNodeStats(selectedCity);
                  return (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-white/5 border border-white/6 p-2">
                        <p className="text-[10px] text-foreground-500/50 uppercase tracking-wider">Ataques/h</p>
                        <p className="text-sm font-heading font-bold text-accent-300">{s.attacksLastHour}</p>
                      </div>
                      <div className="rounded-lg bg-white/5 border border-white/6 p-2">
                        <p className="text-[10px] text-foreground-500/50 uppercase tracking-wider">Bloqueados</p>
                        <p className="text-sm font-heading font-bold text-emerald-400">{s.blocked}</p>
                      </div>
                      <div className="rounded-lg bg-white/5 border border-white/6 p-2">
                        <p className="text-[10px] text-foreground-500/50 uppercase tracking-wider">Activos</p>
                        <p className="text-sm font-heading font-bold text-primary-300">{s.activeNow}</p>
                      </div>
                      <div className="rounded-lg bg-white/5 border border-white/6 p-2">
                        <p className="text-[10px] text-foreground-500/50 uppercase tracking-wider">Uptime</p>
                        <p className="text-sm font-heading font-bold text-foreground-200">{s.uptime}</p>
                      </div>
                    </div>
                  );
                })()}

                <div className="mt-3 pt-2 border-t border-white/6">
                  <p className="text-[10px] text-foreground-500/40">Region: {selectedCity.region}</p>
                </div>
              </div>
            </div>
          )}

          {/* Overlay gradient edges */}
          <div className="absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-[#0a0a14] to-transparent z-30 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#0a0a14] to-transparent z-30 pointer-events-none" />
          <div className="absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-[#0a0a14] to-transparent z-30 pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#0a0a14] to-transparent z-30 pointer-events-none" />

          {/* Aspect ratio spacer */}
          <div className="w-full" style={{ paddingTop: "55%" }} />
        </div>
      </div>

      {/* Floating Tooltip (follows cursor) */}
      {hoveredCity && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: tooltipPos.x + 16,
            top: tooltipPos.y - 16,
          }}
        >
          <div className="bg-background-950/95 backdrop-blur-md border border-foreground-100/10 rounded-lg px-3 py-2 shadow-xl">
            <div className="flex items-center gap-2">
              <span className="text-base leading-none">{flagMap[hoveredCity.flag] || "\uD83C\uDF10"}</span>
              <div>
                <p className="text-xs font-heading font-semibold text-foreground-100">{hoveredCity.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`w-1 h-1 rounded-full ${getStatusColor(hoveredCity.status).dot}`} />
                  <span className={`text-[10px] font-label uppercase tracking-wider ${getStatusColor(hoveredCity.status).text}`}>
                    {hoveredCity.status}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-[9px] text-foreground-500/40 mt-1">Click para detalles del nodo</p>
          </div>
        </div>
      )}

      {/* Live Attacks Panel */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-10 py-5 md:py-6">
        <div className="max-w-[1400px] mx-auto">
          {/* Panel header */}
          <div
            className="flex items-center justify-between px-4 py-2.5 bg-background-950/80 border border-foreground-100/5 rounded-t-xl cursor-pointer select-none"
            onClick={() => setIsPanelOpen(!isPanelOpen)}
          >
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
              <span className="text-xs font-label font-semibold text-foreground-200/80 uppercase tracking-[0.15em]">
                Live attacks
              </span>
              <span className="text-[10px] text-foreground-500/40 font-label">
                {liveAttacks.length} recent
              </span>
            </div>
            <i className={`ri-arrow-down-s-line text-foreground-400/50 transition-transform ${isPanelOpen ? "rotate-0" : "-rotate-90"}`} />
          </div>

          {/* Collapsible scrollable table */}
          <div className={`overflow-hidden transition-all duration-500 ${isPanelOpen ? "max-h-[320px]" : "max-h-0"}`}>
            <div className="overflow-x-auto overflow-y-auto border border-t-0 border-foreground-100/5 rounded-b-xl bg-background-950/60 max-h-[320px]">
              <table className="w-full text-left">
                <thead className="sticky top-0 z-10 bg-background-950/95 backdrop-blur-sm">
                  <tr className="border-b border-foreground-100/5">
                    <th className="px-3 py-2.5 text-[10px] font-label font-semibold text-primary-300/70 uppercase tracking-wider whitespace-nowrap">IP</th>
                    <th className="px-3 py-2.5 text-[10px] font-label font-semibold text-primary-300/70 uppercase tracking-wider whitespace-nowrap">AttackType</th>
                    <th className="px-3 py-2.5 text-[10px] font-label font-semibold text-primary-300/70 uppercase tracking-wider whitespace-nowrap">AttackMsg</th>
                    <th className="px-3 py-2.5 text-[10px] font-label font-semibold text-primary-300/70 uppercase tracking-wider whitespace-nowrap">Source</th>
                    <th className="px-3 py-2.5 text-[10px] font-label font-semibold text-primary-300/70 uppercase tracking-wider whitespace-nowrap">Target</th>
                    <th className="px-3 py-2.5 text-[10px] font-label font-semibold text-primary-300/70 uppercase tracking-wider whitespace-nowrap">Severity</th>
                    <th className="px-3 py-2.5 text-[10px] font-label font-semibold text-primary-300/70 uppercase tracking-wider whitespace-nowrap">Node</th>
                  </tr>
                </thead>
                <tbody>
                  {liveAttacks.slice(0, 20).map((attack, idx) => (
                    <tr
                      key={attack.id}
                      className={`border-b border-foreground-100/[0.02] hover:bg-foreground-100/[0.03] transition-colors ${idx === 0 ? "bg-foreground-100/[0.02]" : ""}`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="text-xs font-mono text-foreground-300/60">{attack.ip}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-label font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${attack.attackTypeClass}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                          {attack.attackType}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs text-foreground-400/50 truncate max-w-[160px] block">{attack.attackMsg}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-base leading-none">{flagMap[attack.sourceFlag] || "\uD83C\uDF10"}</span>
                          <span className="text-xs text-foreground-300/60">{attack.sourceCountry}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-base leading-none">{flagMap[attack.targetFlag] || "\uD83C\uDF10"}</span>
                          <span className="text-xs text-foreground-300/60">{attack.targetCountry}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`text-[11px] font-label font-semibold uppercase tracking-wider ${attack.severityClass}`}>
                          {attack.severity}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-base leading-none">{flagMap[attack.nodeFlag] || "\uD83C\uDF10"}</span>
                          <span className="text-xs text-foreground-300/60">{attack.nodeLocation}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {liveAttacks.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center">
                        <div className="flex items-center justify-center gap-2 text-sm text-foreground-400/40">
                          <i className="ri-loader-4-line animate-spin" />
                          Inicializando monitoreo global...
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom note */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-10 pb-5 text-center">
        <p className="text-[11px] text-foreground-600/30 font-label">
          Datos simulados con fines demostrativos · SOCs activos en multiples paises · Actualizado cada 1.5s
        </p>
      </div>
    </section>
  );
}